import { useMemo } from "react";
import { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import { useScope } from "@state/scopeContext.lib";
import { useWorkspacesList } from "@api/workspace.api";
import { useAccrualEntityIdsWithLoading } from "@api/ledger.api";

/**
 * Whether the client workspace behind a client-specific template has at least
 * one accrual ledger. The scope's workspaceId comes from the SubScopeProvider
 * that wraps the template tasks UI with the template's client workspace (unset
 * for org-wide templates). Fails open (true) for org-wide templates and while
 * the lookups are in flight, so accrual-only options don't flicker out.
 */
function useClientWorkspaceHasAccrualLedger(): boolean {
    const { organizationId, workspaceId } = useScope();
    const { data: workspaces, isLoading: isWorkspacesLoading } =
        useWorkspacesList(
            {
                organizationId: organizationId ?? undefined,
                includeEntities: true,
            },
            { enabled: workspaceId != null && organizationId != null },
        );

    const entityIds = useMemo(() => {
        if (workspaceId == null) {
            return [];
        }
        const workspace = workspaces?.find((ws) => ws.id === workspaceId);
        return (workspace?.entities ?? [])
            .filter((entity) => !entity.isPersonal && !entity.isMockEntity)
            .map((entity) => entity.id);
    }, [workspaces, workspaceId]);

    const { accrualEntityIds, isLoading: isLedgersLoading } =
        useAccrualEntityIdsWithLoading(entityIds);

    if (workspaceId == null) {
        return true;
    }
    if (isWorkspacesLoading || isLedgersLoading) {
        return true;
    }
    return accrualEntityIds.size > 0;
}

/**
 * Whether accrual-ledger-only options (the "Accrual ledger" review field,
 * the Invoice/Bill match filter options, etc.) should be offered for a close
 * template task. False for cash-only (`MODIFIED_CASH_LEDGER`) templates,
 * since those can never have an accrual ledger. Otherwise mirrors
 * `useClientWorkspaceHasAccrualLedger` (fails open for org-wide templates and
 * while the per-workspace lookup is in flight).
 */
export function useIsAccrualLedgerAvailableForTemplate(
    basis?: CloseTemplateBasis | null,
): boolean {
    const hasClientAccrualLedger = useClientWorkspaceHasAccrualLedger();
    return (
        basis !== CloseTemplateBasis.MODIFIED_CASH_LEDGER &&
        hasClientAccrualLedger
    );
}
